import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Play, Pause, ArrowLeft, ArrowUpRight, Download, Mail, FileText, Wrench, Video, Images, Users, Music2 } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";
import { useAudio } from "../context/AudioContext";
import MediaFrame from "../components/MediaFrame";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";

function formatDate(d) {
  return new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function TrackRow({ track, artist }) {
  const { playTrack, toggle, track: current, playing } = useAudio();
  const isCurrent = current?.url === track.url;
  return (
    <button
      data-testid={`track-${track.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      onClick={() => (isCurrent ? toggle() : playTrack({ ...track, artist: artist.name, accent: artist.accent }))}
      className="group w-full flex items-center gap-5 py-5 border-b border-jm-brass/10 text-left"
    >
      <span
        className="w-11 h-11 shrink-0 rounded-full border border-jm-brass/40 flex items-center justify-center text-jm-brass group-hover:bg-jm-amber group-hover:text-jm-ink group-hover:border-jm-amber transition-colors duration-300"
        style={{ borderColor: `${artist.accent}66` }}
      >
        {isCurrent && playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-serif text-lg sm:text-xl text-jm-bone group-hover:text-jm-brass transition-colors duration-300 truncate">
          {track.title}
        </span>
        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mt-1">Extrait audio</span>
      </span>
      <Music2 size={16} className="shrink-0 text-jm-mute group-hover:text-jm-amber transition-colors" />
    </button>
  );
}

function toEmbed(url) {
  if (!url) return null;
  if (url.includes("youtube.com/watch")) {
    try {
      return `https://www.youtube.com/embed/${new URL(url).searchParams.get("v")}`;
    } catch {
      return null;
    }
  }
  if (url.includes("youtu.be/")) return `https://www.youtube.com/embed/${url.split("youtu.be/")[1].split(/[?&]/)[0]}`;
  return null;
}

function MemberBio({ text, testId }) {
  const [open, setOpen] = useState(false);
  const LIMIT = 850;
  if (!text) return null;
  if (text.length <= LIMIT) {
    return <p className="mt-4 text-sm text-jm-smoke leading-relaxed">{text}</p>;
  }
  const preview = text.slice(0, LIMIT);
  return (
    <div className="mt-4">
      <p className="text-sm text-jm-smoke leading-relaxed whitespace-pre-line">
        {open ? text : preview.slice(0, preview.lastIndexOf(" ")) + "…"}
      </p>
      <button
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-jm-brass hover:text-jm-amber transition-colors"
      >
        {open ? "Réduire" : "Lire la suite"}
      </button>
    </div>
  );
}

export default function ArtistDetail() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeVideo, setActiveVideo] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);

  useEffect(() => {
    setArtist(null);
    setNotFound(false);
    api
      .get(`/artists/${slug}`)
      .then((r) => setArtist(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 jm-container text-center" data-testid="artist-not-found">
        <h1 className="font-serif text-4xl text-jm-bone">Projet introuvable</h1>
        <Link to="/artistes" className="mt-6 inline-block text-jm-brass hover:text-jm-amber">
          ← Retour aux projets
        </Link>
      </main>
    );
  }

  if (!artist) {
    return <main className="min-h-screen pt-40 jm-container" data-testid="artist-loading" />;
  }

  const accent = artist.accent || "#C5A059";
  const videoSlots = Array.from({ length: Math.max(4, artist.videos.length) }, (_, i) => artist.videos[i] || { title: `Vidéo ${i + 1}`, url: null });
  const currentVideo = videoSlots[activeVideo] || videoSlots[0];

  const subnavLink = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main data-testid={`artist-page-${artist.id}`}>
      {/* HERO */}
      <section ref={heroRef} className={`relative ${artist.hero_video ? "h-screen" : "h-[72vh]"} min-h-[480px] overflow-hidden`}>
        <motion.div style={{ y: yBg }} className="absolute inset-[-8%]">
          <MediaFrame
            label={artist.name}
            sublabel={artist.genre}
            accent={accent}
            video={artist.hero_video}
            videoWebm={artist.hero_video_webm}
            videoProps={{ autoPlay: true, muted: true, loop: true, controls: true }}
            soundToggle
            image={artist.hero_image}
            imagePosition={artist.hero_position || "center"}
            className="w-full h-full"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-jm-ink via-jm-ink/45 to-transparent" />
        <div className="absolute inset-0 jm-grain opacity-40 pointer-events-none" />
        <div className="absolute top-0 inset-x-0">
          <div className="jm-container pt-28 sm:pt-32">
            <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }}>
              <Link
                to="/artistes"
                data-testid="artist-back-link"
                className="inline-flex items-center gap-2 text-[13px] text-jm-smoke hover:text-jm-bone transition-colors mb-6"
              >
                <ArrowLeft size={14} /> Tous les projets
              </Link>
              <p className="jm-overline mb-4" style={{ color: accent }}>{artist.genre}</p>
              <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl text-jm-bone tracking-wide leading-none">
                {artist.name}
              </h1>
              <p className="mt-5 font-serif italic text-xl sm:text-2xl text-jm-bone/80 max-w-md">{artist.phrase}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={[artist.name, artist.tagline, artist.genre]} />

      {/* SOUS-NAVIGATION */}
      {!artist.minimal && (
      <nav data-testid="artist-subnav" className="sticky top-[73px] sm:top-[89px] z-40 bg-jm-ink/85 backdrop-blur-xl border-b border-jm-brass/15">
        <div className="jm-container flex items-center gap-7 sm:gap-10 overflow-x-auto py-4">
          {[
            { label: "À propos", href: "#a-propos" },
            { label: artist.videos_nav || "Live sessions", href: "#live-sessions" },
            { label: "Bios", href: "#bios" },
            { label: "Presse", href: "#presse" },
            { label: "Shop", to: "/boutique" },
            { label: "Show Contact", href: "#show-contact" },
          ].filter((item) => item.href !== "#bios" || artist.members?.length > 0).map((item) =>
            item.to ? (
              <Link
                key={item.label}
                to={item.to}
                data-testid="subnav-shop"
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-jm-smoke hover:text-jm-amber transition-colors whitespace-nowrap"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => subnavLink(e, item.href)}
                data-testid={`subnav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-jm-smoke hover:text-jm-amber transition-colors whitespace-nowrap"
              >
                {item.label}
              </a>
            )
          )}
        </div>
      </nav>
      )}

      {/* BIO */}
      <section id="a-propos" className="jm-container py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 scroll-mt-32">
        <Reveal className="lg:col-span-4">
          <p className="jm-overline mb-5">Le projet</p>
          <h2 className="font-serif italic font-light text-2xl sm:text-3xl text-jm-bone/90 leading-snug border-l-2 border-jm-amber/50 pl-5">
            «&nbsp;{artist.tagline}&nbsp;»
          </h2>
        </Reveal>
        <div className="lg:col-span-8 space-y-6">
          {artist.bio.map((p, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <p className="text-base sm:text-lg text-jm-smoke leading-relaxed max-w-3xl">{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-5 pt-4">
              {Object.entries(artist.socials || {}).map(([k, v]) => (
                <a
                  key={k}
                  href={v}
                  data-testid={`artist-social-${k}`}
                  className="font-mono text-[11px] uppercase tracking-[0.22em] text-jm-mute hover:text-jm-brass transition-colors"
                >
                  {k}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ACTUS DU PROJET — affichées aussi sur les pages simples (SMALLCO) */}
      {artist.news?.length > 0 && (
        <section className="border-y border-jm-brass/15 bg-jm-coal/40">
          <div className="jm-container py-20 sm:py-24">
            <Reveal>
              <p className="jm-overline mb-8">Actualités</p>
            </Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="artist-news">
              {artist.news.map((n, i) => (
                <Reveal key={n.id} delay={0.07 * i}>
                  <div className="border border-jm-brass/15 bg-jm-card/60 p-7 h-full md:flex md:items-start md:gap-8 lg:block">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jm-mute mb-4 md:mb-0 md:w-40 md:shrink-0 md:pt-1.5">{formatDate(n.date)}</p>
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl text-jm-bone leading-snug">{n.title}</h3>
                      <p className="mt-3 text-sm md:text-base text-jm-smoke leading-relaxed">{n.excerpt}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {!artist.minimal && (
      <>
      {/* VIDÉOS — 4 onglets */}
      <section id="live-sessions" className="border-y border-jm-brass/15 bg-jm-coal/40 scroll-mt-32">
        <div className="jm-container py-20 sm:py-24">
          <Reveal>
            <p className="jm-overline mb-4 flex items-center gap-2"><Video size={13} /> Vidéos</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone mb-4">{artist.videos_title || "En images"}</h2>
            {artist.videos_more_url && (
              <a
                href={artist.videos_more_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="videos-more-link"
                className="inline-block font-mono text-[11px] uppercase tracking-[0.22em] text-jm-amber hover:text-jm-bone transition-colors mb-10"
              >
                Plus sur la chaîne ↗
              </a>
            )}
            {!artist.videos_more_url && <div className="mb-8" />}
          </Reveal>
          <div data-testid="artist-videos">
            <div className="flex flex-wrap gap-2.5 mb-8" data-testid="video-tabs">
              {videoSlots.map((v, i) => (
                <button
                  key={i}
                  data-testid={`video-tab-${i}`}
                  onClick={() => setActiveVideo(i)}
                  className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border transition-colors duration-300 ${
                    activeVideo === i ? "border-jm-amber bg-jm-amber text-jm-ink" : "border-jm-brass/25 text-jm-smoke hover:text-jm-bone"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")} — {v.title}
                </button>
              ))}
            </div>
            <div className="aspect-video border border-jm-brass/15 overflow-hidden" data-testid="video-player-frame">
              {currentVideo.url ? (
                toEmbed(currentVideo.url) ? (
                  <iframe
                    src={toEmbed(currentVideo.url)}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={currentVideo.title}
                  />
                ) : (
                  <video src={currentVideo.url} controls className="w-full h-full object-cover" />
                )
              ) : (
                <MediaFrame
                  label={currentVideo.title}
                  sublabel="Vidéo à venir"
                  accent={accent}
                  className="w-full h-full"
                  testId={`artist-video-placeholder-${activeVideo}`}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ÉCOUTER */}
      {artist.audio_tracks?.length > 0 && (
      <section className="border-y border-jm-brass/15 bg-jm-coal/40">
        <div className="jm-container py-20 sm:py-24">
          <Reveal>
            <p className="jm-overline mb-4">Écouter</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone mb-10">Extraits</h2>
          </Reveal>
          <div data-testid="artist-tracks">
            {artist.audio_tracks.map((t) => (
              <TrackRow key={t.title} track={t} artist={artist} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* DISCOGRAPHIE — masquée si aucun enregistrement renseigné */}
      {artist.discography?.length > 0 && (
      <section className="jm-container py-20 sm:py-28">
        <Reveal>
          <p className="jm-overline mb-4">Discographie</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone mb-12">Enregistrements</h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6" data-testid="artist-discography">
          {artist.discography.map((d, i) => (
            <Reveal key={d.title} delay={0.07 * i}>
              <div className="group">
                <div className="relative overflow-hidden border border-jm-brass/15 group-hover:border-jm-brass/40 transition-colors duration-500">
                  <div className="aspect-square transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]">
                    <MediaFrame label={d.title} sublabel={`${d.type} · ${d.year}`} accent={accent} image={d.image} className="w-full h-full" />
                  </div>
                </div>
                <h3 className="mt-4 font-serif text-lg text-jm-bone">{d.title}</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mt-1">
                  {d.type} — {d.year}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* CONCERTS — liste unique mêlant dates à venir et passées */}
      {artist.concerts?.length > 0 && (() => {
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = artist.concerts.filter((c) => c.date >= today).sort((a, b) => a.date.localeCompare(b.date));
        const past = artist.concerts.filter((c) => c.date < today).sort((a, b) => b.date.localeCompare(a.date));
        const mixed = [
          ...upcoming.map((c) => ({ ...c, _past: false })),
          ...past.map((c) => ({ ...c, _past: true })),
        ];
        return (
          <section className="jm-container py-20 sm:py-28">
            <Reveal>
              <p className="jm-overline mb-4">Sur scène</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone mb-10">{artist.name} sur scène</h2>
            </Reveal>
            <div data-testid="artist-concerts">
              {mixed.map((c, i) => (
                <Reveal key={c.id} delay={0.05 * i}>
                  <div className={`flex flex-wrap items-center gap-4 sm:gap-8 py-6 border-b border-jm-brass/10 ${c._past ? "opacity-60" : ""}`}>
                    <span className="font-mono text-xs text-jm-brass tracking-[0.15em] w-40 shrink-0">{formatDate(c.date)}</span>
                    <span className="flex-1 min-w-[200px] font-serif text-xl text-jm-bone">
                      {c.venue} <span className="text-jm-mute text-base">— {c.city}</span>
                    </span>
                    {c._past ? (
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute">Concert passé</span>
                    ) : c.ticket_url ? (
                      <a
                        href={c.ticket_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`artist-concert-ticket-${c.id}`}
                        className="inline-flex items-center gap-2 border border-jm-brass/40 px-5 py-2.5 text-[13px] text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors"
                      >
                        Réserver <ArrowUpRight size={13} />
                      </a>
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute">Billetterie bientôt</span>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        );
      })()}

      {/* GALERIE — seulement si l'artiste a de vraies photos */}
      {artist.gallery?.length > 0 && (
      <section className="border-y border-jm-brass/15 bg-jm-coal/40">
        <div className="jm-container py-20 sm:py-24">
          <Reveal>
            <p className="jm-overline mb-4 flex items-center gap-2"><Images size={13} /> Galerie</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone mb-12">Photographies</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5" data-testid="artist-gallery">
            {artist.gallery?.length
              ? artist.gallery.map((g, i) => (
                  <Reveal key={g.src} delay={0.05 * (i % 3)}>
                    <div className="group">
                      <MediaFrame
                        label={artist.name}
                        accent={accent}
                        image={g.src}
                        className={`${
                          g.aspect === "video" ? "aspect-video" : g.aspect === "square" ? "aspect-square" : g.aspect === "portrait" ? "aspect-[4/5]" : i % 3 === 0 ? "aspect-[4/5]" : "aspect-square"
                        } border border-jm-brass/15 group-hover:border-jm-brass/40 transition-colors duration-500`}
                        testId={`artist-gallery-${i}`}
                      />
                      {g.credit && (
                        <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.14em] text-jm-mute">{g.credit}</p>
                      )}
                    </div>
                  </Reveal>
                ))
              : Array.from({ length: artist.gallery_count || 6 }).map((_, i) => (
                  <Reveal key={i} delay={0.05 * (i % 3)}>
                    <MediaFrame
                      label={`${artist.name}`}
                      sublabel={`Photographie ${i + 1} — à venir`}
                      accent={accent}
                      className={i % 3 === 0 ? "aspect-[4/5] border border-jm-brass/15" : "aspect-square border border-jm-brass/15"}
                      testId={`artist-gallery-${i}`}
                    />
                  </Reveal>
                ))}
          </div>
        </div>
      </section>
      )}

      {/* MUSICIENS — masqué si aucun musicien renseigné */}
      {artist.members?.length > 0 && (
      <section id="bios" className="jm-container py-20 sm:py-28 scroll-mt-32">
        <Reveal>
          <p className="jm-overline mb-10 flex items-center gap-2"><Users size={13} /> Les musiciens</p>
        </Reveal>
        <div className="border-t border-jm-brass/10" data-testid="artist-members">
          {artist.members.map((m, i) => (
            <Reveal key={m.name} delay={0.05 * i}>
              <div className="group flex flex-col sm:flex-row gap-6 sm:gap-10 py-9 sm:py-10 border-b border-jm-brass/10 items-start">
                {m.photo && (
                <div className="shrink-0">
                  <MediaFrame
                    label={m.name.split(" ").map((w) => w[0]).join("")}
                    sublabel="Portrait à venir"
                    accent={accent}
                    image={m.photo}
                    className="w-[201px] h-[302px] sm:w-[249px] sm:h-[373px] border border-jm-brass/15 group-hover:border-jm-brass/40 transition-colors duration-500"
                    testId={`member-portrait-${i}`}
                  />
                  {m.photo_credit && (
                    <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.14em] text-jm-mute">{m.photo_credit}</p>
                  )}
                </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
                    <h3 className="font-serif text-2xl sm:text-3xl text-jm-bone group-hover:text-jm-brass transition-colors duration-300">
                      {m.name}
                    </h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>
                      {m.instrument}
                    </p>
                  </div>
                  <MemberBio text={m.bio} testId={`member-bio-toggle-${i}`} />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      )}

      {/* ESPACE PRO */}
      <section id="presse" className="border-t border-jm-brass/15 scroll-mt-32">
        {artist.press_quotes?.length > 0 && (
          <div className="jm-container pt-20 sm:pt-24" data-testid="press-quotes">
            <Reveal>
              <p className="jm-overline mb-4">Revue de presse</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone mb-12">La presse en parle</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
              {artist.press_quotes.map((q, i) => (
                <Reveal key={i} delay={0.05 * (i % 2)}>
                  <blockquote
                    className="border-l-2 border-jm-amber/50 pl-6 h-full"
                    data-testid={`press-quote-${i}`}
                  >
                    <p className="font-serif italic font-light text-lg sm:text-xl text-jm-bone/90 leading-relaxed">
                      «&nbsp;{q.quote}&nbsp;»
                    </p>
                    <footer className="mt-5">
                      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jm-brass">{q.author}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jm-mute mt-1.5">{q.media}</p>
                    </footer>
                  </blockquote>
                </Reveal>
              ))}
            </div>
          </div>
        )}
        <div className="jm-container py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <p className="jm-overline mb-4">Espace professionnel</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone leading-tight">
              Programmateurs, journalistes, labels
            </h2>
            <p className="mt-5 text-jm-smoke leading-relaxed max-w-xl">
              Dossier de presse, fiche technique, photos HD et liens d'écoute : tout est réuni pour préparer
              une venue ou une publication.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5 flex flex-col gap-3">
            {artist.press_file && (
            <a
              href={artist.press_file}
              target="_blank"
              rel="noreferrer"
              data-testid="download-press-kit"
              className="inline-flex items-center justify-between gap-3 border border-jm-brass/30 px-6 py-4 text-sm text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors"
            >
              <span className="inline-flex items-center gap-3"><FileText size={16} /> Dossier de presse</span>
              <Download size={15} />
            </a>
            )}
            {(artist.tech_files || []).map((f, i) => (
            <a
              key={f.url}
              href={f.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`download-tech-rider-${i}`}
              className="inline-flex items-center justify-between gap-3 border border-jm-brass/30 px-6 py-4 text-sm text-jm-bone hover:border-jm-amber hover:text-jm-amber transition-colors"
            >
              <span className="inline-flex items-center gap-3"><Wrench size={16} /> {f.label}</span>
              <Download size={15} />
            </a>
            ))}
            <a
              id="show-contact"
              href={`mailto:${artist.press_email}`}
              data-testid="artist-press-contact"
              className="inline-flex items-center justify-between gap-3 bg-jm-amber text-jm-ink px-6 py-4 text-sm font-medium hover:bg-jm-gold transition-colors"
            >
              <span className="inline-flex items-center gap-3"><Mail size={16} /> Contact professionnel</span>
              <ArrowUpRight size={15} />
            </a>
          </Reveal>
        </div>
      </section>
      </>
      )}
    </main>
  );
}
